import { AxiosError } from 'axios';
import { loadConfig } from '../../utils/configLoader';
const DEFAULT_ATTEMPT: number = 2;

export type Pais = 'AR' | 'UY' | 'BR';

/**
 * País REAL del build, derivado del dominio (o de REACT_APP_PAIS en dev).
 * Misma lógica que ConfigContext.getPaisFromDominio, pero usable fuera de React
 * (servicios). Es la señal de país canónica para rutear leads de empresas:
 * AR -> BFF (§12.2), UY -> monolito legacy. El BFF es Argentina-only.
 */
export const getPais = (): Pais => {
  if (process.env.REACT_APP_DEV_MODE === 'true') {
    const pais = (process.env.REACT_APP_PAIS || 'AR').replace(/"/g, '');
    return pais.toUpperCase() as Pais;
  }
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname.endsWith('.com.uy')) return 'UY';
  if (hostname.endsWith('.com.br')) return 'BR';
  return 'AR';
};

/**
 * TRANSITORIO (convivencia IIS + Kubernetes): sacar cuando el monolito deje de resolver
 * el origen del partner por el host del request.
 *
 * El monolito sabe de qué partner viene la cotización por el subdominio
 * (cencopay.segurarse.com.ar). En el contenedor el endpoint es una URL fija
 * (APP_ENDPOINT), así que entrando por <partner>.front.<dominio> se le pegaba al
 * monolito sin partner. Si el host del browser trae un partner antes de "front.",
 * se lo antepone al host del endpoint: cencopay.front.qa.segurarse.com.ar ->
 * https://cencopay.qa.segurarse.com.ar/admin/api. Sin partner (front.qa..., IIS,
 * localhost) devuelve el endpoint tal cual.
 */
export const endpointDelPartner = (endpoint: string, hostname: string): string => {
  const partner = /^(.+?)\.front\./i.exec(hostname)?.[1];
  if (!partner) return endpoint;

  const url = new URL(endpoint);
  const host = url.hostname.replace(/^www\./i, "");
  if (host.toLowerCase().startsWith(partner.toLowerCase() + ".")) return endpoint;

  url.hostname = `${partner}.${host}`;
  return url.origin + url.pathname + url.search;
};

/**
 * URL del API del monolito (config.endpoint). Acepta dos formas:
 * - URL completa (https://qa.segurarse.com.ar/admin/api): el contenedor, que ya no vive
 *   en el dominio del monolito. Se usa tal cual (salvo el partner del host, ver
 *   endpointDelPartner); sin base_endpoint, solo su origen.
 * - Path (/admin/api): el deploy dentro del monolito, cuyo react_config.json lo sirve
 *   el monolito. Se concatena al origen del browser (o al host de dev).
 */
export const getPathName = async (base_endpoint = true): Promise<string> => {

  const config = await loadConfig()
  const endpoint = config?.endpoint ?? "";

  if (/^https?:\/\//i.test(endpoint)) {
    const delPartner = endpointDelPartner(endpoint, window.location.hostname);
    return base_endpoint ? delPartner.replace(/\/+$/, "") : new URL(delPartner).origin;
  }

  const baseUrl = window.location.origin;

  if (process.env.REACT_APP_DEV_MODE === "true" || window.location.hostname === "localhost") {

    const end_hostname = process.env.REACT_APP_PAIS || "ar"

    return process.env.REACT_APP_LOCAL_HOSTNAME + "." + end_hostname + (base_endpoint ? config!.endpoint : "");
  }

  return baseUrl + (base_endpoint ? config!.endpoint : "");
}

export async function retryRequest<T>(
  axiosRequest: () => Promise<T>,
  retries: number = DEFAULT_ATTEMPT
): Promise<T> {
  let attempt = 0;

  while (attempt <= retries) {
    try {
      const response = await axiosRequest();
      return response; // Si la solicitud tiene éxito, retorna el resultado
    } catch (error: any) {
      attempt++;
      if (attempt > retries || !isRetryableError(error)) {
        throw error; // Si se alcanzan los reintentos, lanza el error
      }
    }
  }

  throw new Error('Max retries reached'); // Solo por seguridad, aunque no debería llegar aquí
}

function isRetryableError(error: AxiosError): boolean {
  // Puedes definir qué errores son "reintetables"
  return error.response?.status === 500 || error.response?.status === 503;
}

/**
 * Convierte un objeto Date en una cadena de texto con el formato `DD/MM/YYYY`.
 *
 * @param {Date} date - La fecha que se va a formatear.
 * @returns {string} La fecha formateada como `DD/MM/YYYY`.
 *
 * @example
 * const date = new Date('2024-08-02T00:00:00');
 * const formattedDate = formatDate(date);
 * console.log(formattedDate); // "02/08/2024"
 */
export function formatDate(date: Date) {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

/**
* Convierte un objeto Date en una cadena de texto con el formato `HH : mm`.
*
* @param {Date} date - La fecha que se va a formatear.
* @returns {string} La hora formateada como `HH : mm`.
*
* @example
* const date = new Date('2024-08-02T10:58:00');
* const formattedTime = formatHora(date);
* console.log(formattedTime); // "10 : 58"
*/
export function formatHora(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0'); // Asegura que las horas tengan dos dígitos
  const minutes = date.getMinutes().toString().padStart(2, '0'); // Asegura que los minutos tengan dos dígitos

  return `${hours} : ${minutes}`;
}

export const getRecaptchaKey = async () => {
  const config = await loadConfig();
  let recaptcha_key = config?.recaptcha_key;
  if(!recaptcha_key){
    // Sin recaptcha_key en la config, se elige por el país real (dominio). Antes se
    // miraba config.pais, que el react_config.json generado en el contenedor no trae.
    recaptcha_key = getPais() === "AR" ? "6LfJFiIpAAAAADbH-UpNwaQr-kealDIT1_zH6p0r" : "6LdS3oMrAAAAAM-NkeBSmjwc7rNN4stPKRkkmrtW"
  }
  if (process.env.REACT_APP_DEV_MODE === "true") {
    if (process.env.REACT_APP_DEV_PAIS === "uy") {
      return process.env.REACT_APP_RECAPTCHA_KEY_UY;
    } else {
      return process.env.REACT_APP_RECAPTCHA_KEY_AR;
    }
  }
  return recaptcha_key;
};

export const getBffBaseUrl = async (): Promise<string> => {
  const config = await loadConfig();
  const base = config?.bff_url || process.env.REACT_APP_API_BASE; // react_config manda; .env como fallback de dev
  if (!base) {
    throw new Error('bff_url no está configurado en react_config.json');
  }
  return base.replace(/\/+$/, '');
};

/**
 * Credenciales del API del monolito (hash + JWT). Salen de react_config.json
 * (config.hash / config.token), que en el contenedor se genera desde las
 * variables de entorno al arrancar. process.env queda solo como fallback de
 * desarrollo local (.env.local, no versionado).
 */
export const getApiHash = async (): Promise<string> => {
  const config = await loadConfig();
  return config?.hash || process.env.REACT_APP_HASH || "";
};

export const getApiToken = async (): Promise<string> => {
  const config = await loadConfig();
  return config?.token || process.env.REACT_APP_TOKEN || "";
};
